import React, { useState, useRef, useEffect } from 'react';
import emailjs from 'emailjs-com';
import { Mail, Phone, MapPin, Send, Loader2, CheckCircle2, AlertCircle } from 'lucide-react';
import FounderCard from './Founder';

const SERVICE_ID = process.env.REACT_APP_EMAILJS_SERVICE_ID;
const TEMPLATE_ID = process.env.REACT_APP_EMAILJS_TEMPLATE_ID;
const PUBLIC_KEY = process.env.REACT_APP_EMAILJS_USER_ID;

if (PUBLIC_KEY) {
    emailjs.init(PUBLIC_KEY);
}

const ContactSection = () => {
    const [form, setForm] = useState({ name: '', email: '', message: '' });
    const [status, setStatus] = useState('idle'); // idle | sending | sent | error
    const [errorMsg, setErrorMsg] = useState('');

    // Analytics (public/vb-analytics.js): which fields were filled, never their contents.
    const formTrack = useRef({ started: false, submitted: false, filled: {} });
    const vb = (event, props, opts) => { try { if (window.vbTrack) window.vbTrack(event, props, opts); } catch (e) { /* ignore */ } };
    const filledList = () => ['name', 'email', 'message'].filter((k) => formTrack.current.filled[k]);
    const markStarted = (field) => {
        if (formTrack.current.started) return;
        formTrack.current.started = true;
        vb('contact_form_started', { first_field: field });
    };
    const handleBlur = (e) => {
        const { name, value } = e.target;
        if (!value.trim() || formTrack.current.filled[name]) return;
        formTrack.current.filled[name] = true;
        vb('contact_form_field_filled', { field: name, fields_filled: filledList(), completion: Math.round((filledList().length / 3) * 100) });
    };
    useEffect(() => {
        const onLeave = () => {
            const t = formTrack.current;
            if (t.started && !t.submitted && !t.abandonSent) {
                t.abandonSent = true;
                vb('contact_form_abandoned', { fields_filled: filledList(), completion: Math.round((filledList().length / 3) * 100) }, { transport: 'sendBeacon' });
            }
        };
        const onHidden = () => { if (document.visibilityState === 'hidden') onLeave(); };
        window.addEventListener('pagehide', onLeave);
        document.addEventListener('visibilitychange', onHidden);
        return () => { window.removeEventListener('pagehide', onLeave); document.removeEventListener('visibilitychange', onHidden); };
    }, []);

    const handleChange = (e) => {
        const { name, value } = e.target;
        markStarted(name);
        setForm((prev) => ({ ...prev, [name]: value }));
    };

    const handleSubmit = async (e) => {
        e.preventDefault();

        const name = form.name.trim();
        const email = form.email.trim();
        const message = form.message.trim();

        if (!name || !email || !message) {
            vb('contact_form_error', { reason: 'missing_fields', fields_filled: ['name', 'email', 'message'].filter((k) => form[k].trim()) });
            setStatus('error');
            setErrorMsg('Please fill in your name, email, and message.');
            return;
        }

        if (!SERVICE_ID || !TEMPLATE_ID || !PUBLIC_KEY) {
            setStatus('error');
            setErrorMsg('Email service is not configured. Please contact us directly.');
            return;
        }

        setStatus('sending');
        setErrorMsg('');

        const composedMessage = `Email: ${email}\nMessage: ${message}`;

        try {
            await emailjs.send(SERVICE_ID, TEMPLATE_ID, {
                from_name: name,
                from_email: email,
                message: composedMessage,
                to_email: 'support@vetobase.com',
            });
            formTrack.current.submitted = true;
            vb('contact_form_submitted', { fields_filled: ['name', 'email', 'message'], message_length: message.length });
            try { if (window.posthog && window.posthog.identify) window.posthog.identify(email.toLowerCase(), { email: email.toLowerCase(), name }); } catch (e2) { /* ignore */ }
            setStatus('sent');
            setForm({ name: '', email: '', message: '' });
            setTimeout(() => setStatus('idle'), 4000);
        } catch (err) {
            vb('contact_form_error', { reason: 'send_failed' });
            setStatus('error');
            setErrorMsg(err?.text || 'Something went wrong. Please try again.');
        }
    };

    return (
        <section id="contact" className="w-full py-20 bg-card scroll-mt-24">
            <div className="max-w-6xl mx-auto px-6 md:px-10">
                <div className="flex flex-col items-center gap-3 text-center mb-12">
                    <span className="text-textMuted font-mono uppercase tracking-label text-[10px]">Get in Touch</span>
                    <h2 className="text-2xl md:text-5xl font-bold text-ink tracking-head">Contact Us</h2>
                    <p className="text-textSecondary max-w-xl text-base md:text-lg">
                        Have questions about Vetobase AI? Send us a message — our team usually replies within a business day.
                    </p>
                </div>

                <div className="grid grid-cols-1 lg:grid-cols-5 gap-8">
                    {/* Contact details */}
                    <div className="lg:col-span-2 flex flex-col gap-4">
                        <a
                            href="mailto:support@vetobase.com"
                            className="flex items-start gap-4 p-5 rounded-2xl bg-card border border-borderLight hover:shadow-hard-soft transition-all"
                        >
                            <div className="w-11 h-11 rounded-2xl bg-bone flex items-center justify-center flex-shrink-0">
                                <Mail className="w-5 h-5 text-ink" />
                            </div>
                            <div className="flex flex-col">
                                <span className="text-[10px] font-mono text-textMuted uppercase tracking-label">Email</span>
                                <span className="text-base font-semibold text-ink break-all">support@vetobase.com</span>
                            </div>
                        </a>

                        <a
                            href="tel:+923124012324"
                            className="flex items-start gap-4 p-5 rounded-2xl bg-card border border-borderLight hover:shadow-hard-soft transition-all"
                        >
                            <div className="w-11 h-11 rounded-2xl bg-bone flex items-center justify-center flex-shrink-0">
                                <Phone className="w-5 h-5 text-ink" />
                            </div>
                            <div className="flex flex-col">
                                <span className="text-[10px] font-mono text-textMuted uppercase tracking-label">Phone &amp; WhatsApp</span>
                                <span className="text-base font-semibold text-ink">+92 312 4012324</span>
                            </div>
                        </a>

                        <div className="flex items-start gap-4 p-5 rounded-2xl bg-card border border-borderLight">
                            <div className="w-11 h-11 rounded-2xl bg-bone flex items-center justify-center flex-shrink-0">
                                <MapPin className="w-5 h-5 text-ink" />
                            </div>
                            <div className="flex flex-col">
                                <span className="text-[10px] font-mono text-textMuted uppercase tracking-label">Registered office</span>
                                <span className="text-base font-semibold text-ink">Vetobase (SMC-Private) Limited</span>
                                <span className="text-sm text-textSecondary leading-relaxed">1032 Ravi Block, Allama Iqbal Town, Lahore 54000, Punjab, Pakistan</span>
                            </div>
                        </div>

                        <FounderCard />
                    </div>

                    {/* Form */}
                    <form
                        id="contact-form"
                        data-placement="contact_form"
                        onSubmit={handleSubmit}
                        className="lg:col-span-3 flex flex-col gap-4 bg-card border border-borderLight rounded-card p-6 md:p-8"
                    >
                        <div className="flex flex-col gap-2">
                            <label htmlFor="name" className="text-[11px] font-mono text-textSecondary uppercase tracking-label">
                                Name
                            </label>
                            <input
                                id="name"
                                name="name"
                                type="text"
                                required
                                value={form.name}
                                onChange={handleChange}
                                onFocus={(e) => markStarted(e.target.name)}
                                onBlur={handleBlur}
                                placeholder="Your name"
                                disabled={status === 'sending'}
                                className="w-full px-4 py-3 rounded-2xl bg-card border border-borderLight text-ink placeholder:text-textMuted focus:outline-none focus:border-ink transition-all"
                            />
                        </div>

                        <div className="flex flex-col gap-2">
                            <label htmlFor="email" className="text-[11px] font-mono text-textSecondary uppercase tracking-label">
                                Email
                            </label>
                            <input
                                id="email"
                                name="email"
                                type="email"
                                required
                                value={form.email}
                                onChange={handleChange}
                                onFocus={(e) => markStarted(e.target.name)}
                                onBlur={handleBlur}
                                placeholder="you@example.com"
                                disabled={status === 'sending'}
                                className="w-full px-4 py-3 rounded-2xl bg-card border border-borderLight text-ink placeholder:text-textMuted focus:outline-none focus:border-ink transition-all"
                            />
                        </div>

                        <div className="flex flex-col gap-2">
                            <label htmlFor="message" className="text-[11px] font-mono text-textSecondary uppercase tracking-label">
                                Message
                            </label>
                            <textarea
                                id="message"
                                name="message"
                                required
                                rows={5}
                                value={form.message}
                                onChange={handleChange}
                                onFocus={(e) => markStarted(e.target.name)}
                                onBlur={handleBlur}
                                placeholder="How can we help?"
                                disabled={status === 'sending'}
                                className="w-full px-4 py-3 rounded-2xl bg-card border border-borderLight text-ink placeholder:text-textMuted focus:outline-none focus:border-ink transition-all resize-none"
                            />
                        </div>

                        <button
                            type="submit"
                            disabled={status === 'sending'}
                            className="mt-2 inline-flex items-center justify-center gap-2 bg-ink text-paper px-6 py-3.5 rounded-full text-base font-bold shadow-hard-soft transition-all active:scale-95 disabled:opacity-60 disabled:cursor-not-allowed"
                        >
                            {status === 'sending' ? (
                                <>
                                    <Loader2 className="w-5 h-5 animate-spin" />
                                    Sending...
                                </>
                            ) : status === 'sent' ? (
                                <>
                                    <CheckCircle2 className="w-5 h-5" />
                                    Sent!
                                </>
                            ) : (
                                <>
                                    Send Message
                                    <Send className="w-4 h-4" />
                                </>
                            )}
                        </button>

                        {status === 'sent' && (
                            <div className="flex items-center gap-2 text-success text-sm font-medium">
                                <CheckCircle2 className="w-4 h-4" />
                                Thanks! Your message has been sent to support@vetobase.com.
                            </div>
                        )}
                        {status === 'error' && (
                            <div className="flex items-center gap-2 text-danger text-sm font-medium">
                                <AlertCircle className="w-4 h-4" />
                                {errorMsg}
                            </div>
                        )}
                    </form>
                </div>
            </div>
        </section>
    );
};

export default ContactSection;
